import { useState } from "react";
import type { ActivityGenerationSettings, Model } from "@lumen/shared";
import { t } from "../../i18n";
import {
  scenarioInputDefinitions,
  waveSpeedInputDefinitions,
} from "./ComposerParameterDefinitions";

export function ComposerDynamicParameters({
  selectedModel,
  settings,
  onChange,
}: {
  selectedModel?: Model;
  settings: ActivityGenerationSettings;
  onChange: (settings: ActivityGenerationSettings) => void;
}) {
  const isScenarioModel =
    selectedModel?.providerName?.toLowerCase().includes("scenario") ?? false;
  const scenarioInputs = isScenarioModel
    ? scenarioInputDefinitions(selectedModel)
    : [];
  const isWaveSpeedModel = Boolean(
    selectedModel &&
    (selectedModel.providerName?.toLowerCase().includes("wavespeed") ||
      selectedModel.name.toLowerCase().includes("wavespeed") ||
      selectedModel.remoteId.toLowerCase().startsWith("wavespeed-ai/")),
  );
  const waveSpeedInputs = isWaveSpeedModel
    ? waveSpeedInputDefinitions(selectedModel)
    : [];
  const [scenarioJsonDrafts, setScenarioJsonDrafts] = useState<
    Record<string, string>
  >({});
  const update = (patch: Partial<ActivityGenerationSettings>) =>
    onChange({ ...settings, ...patch });
  const updateScenarioParameter = (name: string, value: unknown) =>
    update({
      scenarioParameters: {
        ...(settings.scenarioParameters ?? {}),
        [name]: value,
      },
    });
  const updateWaveSpeedParameter = (name: string, value: unknown) =>
    update({
      waveSpeedParameters: {
        ...(settings.waveSpeedParameters ?? {}),
        [name]: value,
      },
    });
  return (
    <>
      {isScenarioModel && scenarioInputs.length > 0 && (
        <div className="space-y-2 rounded-lg border border-sky-200 bg-sky-50/50 p-3 dark:border-sky-900/50 dark:bg-sky-950/20">
          <p className="text-[11px] font-medium text-stone-600 dark:text-stone-300">
            {t("Scenario model parameters")}
          </p>
          <p className="text-[11px] text-stone-500">
            {t(
              "These controls come from the selected Scenario model. Reference-image fields use images attached to the request automatically.",
            )}
          </p>
          {scenarioInputs
            .filter((input) => input.name !== "prompt")
            .map((input) => {
              const name = String(input.name);
              const type = String(input.type ?? "string");
              const current =
                settings.scenarioParameters?.[name] ?? input.default;
              const allowed = Array.isArray(input.allowedValues)
                ? input.allowedValues
                : [];
              const label =
                typeof input.label === "string" ? input.label : name;
              const description =
                typeof input.description === "string"
                  ? input.description
                  : undefined;
              if (type === "boolean") {
                return (
                  <label key={name} className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={Boolean(current)}
                      onChange={(event) =>
                        updateScenarioParameter(name, event.target.checked)
                      }
                    />
                    {label}
                  </label>
                );
              }
              if (type === "file" || type === "file_array") {
                return (
                  <div
                    key={name}
                    className="rounded border border-dashed border-sky-300 p-2 text-xs text-stone-500"
                  >
                    <span className="font-medium text-stone-700 dark:text-stone-300">
                      {label}
                    </span>
                    : {t("Attach the required image to the message")}
                  </div>
                );
              }
              if (allowed.length > 0) {
                return (
                  <label key={name} className="block">
                    <span className="label">{label}</span>
                    <select
                      className="field"
                      value={current === undefined ? "" : String(current)}
                      onChange={(event) =>
                        updateScenarioParameter(
                          name,
                          event.target.value || undefined,
                        )
                      }
                    >
                      <option value="">{t("Provider default")}</option>
                      {allowed.map((option) => (
                        <option key={String(option)} value={String(option)}>
                          {String(option)}
                        </option>
                      ))}
                    </select>
                    {description && (
                      <span className="mt-1 block text-[10px] text-stone-500">
                        {description}
                      </span>
                    )}
                  </label>
                );
              }
              if (type === "number") {
                return (
                  <label key={name} className="block">
                    <span className="label">{label}</span>
                    <input
                      type="number"
                      className="field"
                      min={
                        typeof input.min === "number" ? input.min : undefined
                      }
                      max={
                        typeof input.max === "number" ? input.max : undefined
                      }
                      step={typeof input.step === "number" ? input.step : 1}
                      value={typeof current === "number" ? current : ""}
                      placeholder={
                        typeof input.placeholder === "string"
                          ? input.placeholder
                          : t("Provider default")
                      }
                      onChange={(event) =>
                        updateScenarioParameter(
                          name,
                          event.target.value
                            ? Number(event.target.value)
                            : undefined,
                        )
                      }
                    />
                    {description && (
                      <span className="mt-1 block text-[10px] text-stone-500">
                        {description}
                      </span>
                    )}
                  </label>
                );
              }
              if (type === "inputs_array") {
                return (
                  <label key={name} className="block">
                    <span className="label">{label}</span>
                    <textarea
                      className="field min-h-16"
                      value={
                        scenarioJsonDrafts[name] ??
                        (Array.isArray(current) ||
                        (current && typeof current === "object")
                          ? JSON.stringify(current)
                          : "")
                      }
                      placeholder={t("JSON value")}
                      onChange={(event) => {
                        const raw = event.target.value;
                        setScenarioJsonDrafts((drafts) => ({
                          ...drafts,
                          [name]: raw,
                        }));
                        try {
                          updateScenarioParameter(
                            name,
                            raw ? JSON.parse(raw) : undefined,
                          );
                        } catch {
                          /* Wait for valid JSON before persisting. */
                        }
                      }}
                    />
                    {description && (
                      <span className="mt-1 block text-[10px] text-stone-500">
                        {description}
                      </span>
                    )}
                  </label>
                );
              }
              if (
                type === "string_array" ||
                type === "number_array" ||
                type === "model_array"
              ) {
                const values = Array.isArray(current) ? current.join(", ") : "";
                return (
                  <label key={name} className="block">
                    <span className="label">{label}</span>
                    <input
                      className="field"
                      value={values}
                      placeholder={t("Comma-separated values")}
                      onChange={(event) =>
                        updateScenarioParameter(
                          name,
                          event.target.value
                            ? event.target.value
                                .split(",")
                                .map((value) => value.trim())
                                .filter(Boolean)
                                .map((value) =>
                                  type === "number_array"
                                    ? Number(value)
                                    : value,
                                )
                            : undefined,
                        )
                      }
                    />
                    {description && (
                      <span className="mt-1 block text-[10px] text-stone-500">
                        {description}
                      </span>
                    )}
                  </label>
                );
              }
              return (
                <label key={name} className="block">
                  <span className="label">{label}</span>
                  <input
                    className="field"
                    value={
                      typeof current === "string" || typeof current === "number"
                        ? String(current)
                        : ""
                    }
                    placeholder={
                      typeof input.placeholder === "string"
                        ? input.placeholder
                        : t("Provider default")
                    }
                    onChange={(event) =>
                      updateScenarioParameter(
                        name,
                        event.target.value || undefined,
                      )
                    }
                  />
                  {description && (
                    <span className="mt-1 block text-[10px] text-stone-500">
                      {description}
                    </span>
                  )}
                </label>
              );
            })}
        </div>
      )}
      {isWaveSpeedModel && waveSpeedInputs.length > 0 && (
        <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-900/50 dark:bg-amber-950/20">
          <p className="text-[11px] font-medium text-stone-600 dark:text-stone-300">
            {t("WaveSpeedAI model parameters")}
          </p>
          <p className="text-[11px] text-stone-500">
            {t(
              "These controls come from the selected WaveSpeedAI model. Image inputs use images attached to the request automatically.",
            )}
          </p>
          {waveSpeedInputs
            .filter(
              ({ name }) =>
                ![
                  "prompt",
                  "text",
                  "image",
                  "image_url",
                  "input_image_url",
                  "reference_image",
                  "back_image_url",
                  "left_image_url",
                  "right_image_url",
                  "output_format",
                  "format",
                  "topology",
                  "polygon_type",
                  "target_polycount",
                  "targetPolycount",
                  "face_count",
                  "face_limit",
                  "target_face_num",
                  "texture_prompt",
                  "enable_pbr",
                  "pbr",
                  "should_texture",
                  "texture",
                ].includes(name),
            )
            .map(({ name, schema }) => {
              const current =
                settings.waveSpeedParameters?.[name] ?? schema.default;
              const label =
                typeof schema.title === "string"
                  ? schema.title
                  : name.replace(/_/g, " ");
              const description =
                typeof schema.description === "string"
                  ? schema.description
                  : undefined;
              const allowed = Array.isArray(schema.enum) ? schema.enum : [];
              const type = String(
                schema.type ?? (allowed.length ? "string" : "string"),
              );
              if (["image", "file", "file_url"].includes(type))
                return (
                  <div
                    key={name}
                    className="rounded border border-dashed border-amber-300 p-2 text-xs text-stone-500"
                  >
                    <span className="font-medium text-stone-700 dark:text-stone-300">
                      {label}
                    </span>
                    : {t("Attach the required image to the message")}
                  </div>
                );
              if (type === "boolean")
                return (
                  <label key={name} className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={Boolean(current)}
                      onChange={(event) =>
                        updateWaveSpeedParameter(name, event.target.checked)
                      }
                    />
                    {label}
                  </label>
                );
              if (allowed.length > 0)
                return (
                  <label key={name} className="block">
                    <span className="label">{label}</span>
                    <select
                      className="field"
                      value={current === undefined ? "" : String(current)}
                      onChange={(event) =>
                        updateWaveSpeedParameter(
                          name,
                          event.target.value || undefined,
                        )
                      }
                    >
                      <option value="">{t("Provider default")}</option>
                      {allowed.map((option) => (
                        <option key={String(option)} value={String(option)}>
                          {String(option)}
                        </option>
                      ))}
                    </select>
                    {description && (
                      <span className="mt-1 block text-[10px] text-stone-500">
                        {description}
                      </span>
                    )}
                  </label>
                );
              if (type === "integer" || type === "number")
                return (
                  <label key={name} className="block">
                    <span className="label">{label}</span>
                    <input
                      type="number"
                      className="field"
                      min={
                        typeof schema.minimum === "number"
                          ? schema.minimum
                          : undefined
                      }
                      max={
                        typeof schema.maximum === "number"
                          ? schema.maximum
                          : undefined
                      }
                      step={type === "integer" ? 1 : "any"}
                      value={typeof current === "number" ? String(current) : ""}
                      placeholder={t("Provider default")}
                      onChange={(event) =>
                        updateWaveSpeedParameter(
                          name,
                          event.target.value
                            ? Number(event.target.value)
                            : undefined,
                        )
                      }
                    />
                    {description && (
                      <span className="mt-1 block text-[10px] text-stone-500">
                        {description}
                      </span>
                    )}
                  </label>
                );
              return (
                <label key={name} className="block">
                  <span className="label">{label}</span>
                  <input
                    className="field"
                    value={
                      typeof current === "string" || typeof current === "number"
                        ? String(current)
                        : ""
                    }
                    placeholder={t("Provider default")}
                    onChange={(event) =>
                      updateWaveSpeedParameter(
                        name,
                        event.target.value || undefined,
                      )
                    }
                  />
                  {description && (
                    <span className="mt-1 block text-[10px] text-stone-500">
                      {description}
                    </span>
                  )}
                </label>
              );
            })}
        </div>
      )}
    </>
  );
}
